import React, { useEffect, useState } from 'react';
import { Menu, X, Mail, Phone, MapPin } from 'lucide-react';
import { Toaster } from 'sonner';

const WHATSAPP_NUMBER = '6285760070056';
const WHATSAPP_MESSAGE = encodeURIComponent('Halo BauBagah Maju, saya tertarik untuk konsultasi project.');

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    let rafId = null;

    const onScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        setIsScrolled(window.scrollY > 10);
      });
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`, '_blank');
  };

  const services = [
    { icon: '/assets/Icon Web Development.svg', title: 'Website Development' },
    { icon: '/assets/Icon UI UX.svg', title: 'UI / UX Consultant' },
    { icon: '/assets/Icon App.svg', title: 'Android / iOS Platform' },
    { icon: '/assets/Icon Dev Ops.svg', title: 'Dev Ops' },
    { icon: '/assets/Icon Business Consultant.svg', title: 'Business Consultant' },
    { icon: '/assets/Icon IOT Dev.svg', title: 'IOT Development' }
  ];

  const testimonials = [
    {
      text: "Their expertise and dedication have exceeded our expectations. From start to finish, they demonstrated a deep understanding of our needs and delivered outstanding results.",
      name: "Zulfan Fajar",
      role: "CEO, Jumpapay",
      image: "/assets/Profile Zulfan Fajar.png"
    },
    {
      text: "BBM has been a great partner. They made our work much easier and more efficient with their smart IT solutions.",
      name: "Mendi Susanto",
      role: "CEO, Data Labs Analytics",
      image: "/assets/Profile Mendi Susanto.png"
    },
    {
      text: "BBM sets the bar high when it comes to IT services. Their exceptional performance, proactive problem-solving, and seamless collaboration have elevated our business to new heights.",
      name: "Dikry Paren",
      role: "CEO, Qazwa",
      image: "/assets/Profile Dikry Paren.png"
    }
  ];

  return (
    <div className="landing-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className={`header ${isScrolled ? 'scrolled' : 'top'}`} data-testid="header">
        <div className="header-inner">
          <div className="header-logo">
            <img src="/assets/Logo BBM Color.png" alt="BauBagah Maju" />
          </div>
          
          <nav className={`header-nav ${mobileMenuOpen ? 'active' : ''}`}>
            <a href="#works" onClick={(e) => { e.preventDefault(); scrollToSection('works'); }} data-testid="nav-our-work">Our Work</a>
            <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} data-testid="nav-our-services">Our Services</a>
            <a href="#why-us" onClick={(e) => { e.preventDefault(); scrollToSection('why-us'); }} data-testid="nav-why-us">Why Us</a>
          </nav>
          
          <button className="btn-primary" onClick={openWhatsApp} data-testid="header-lets-talk-btn">
            Let's talk
          </button>
          
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" data-testid="hero-section">
        <div className="hero-top">
          <div className="hero-text">
            <h1>
              Scale Your Business With
              <span>High Performance Software</span>
            </h1>
            <button className="btn-cta" onClick={openWhatsApp} data-testid="hero-start-project-btn">
              Start Your Project
            </button>
          </div>
          
          <div className="hero-trust">
            <p>Trusted by big companies, government, and startup</p>
            <div className="trust-logos-wrapper">
              <div className="trust-logos">
                <img src="/assets/Logo BNI.png" alt="BNI" />
                <img src="/assets/Logo Jumpapay.png" alt="Jumpapay" />
                <img src="/assets/Logo Qazwa.png" alt="Qazwa" />
                <img src="/assets/Logo First Cloud.png" alt="First Cloud" />
                <img src="/assets/Logo Pegadaian.png" alt="Pegadaian" />
                {/* Duplicate for seamless loop */}
                <img src="/assets/Logo BNI.png" alt="BNI" />
                <img src="/assets/Logo Jumpapay.png" alt="Jumpapay" />
                <img src="/assets/Logo Qazwa.png" alt="Qazwa" />
                <img src="/assets/Logo First Cloud.png" alt="First Cloud" />
                <img src="/assets/Logo Pegadaian.png" alt="Pegadaian" />
              </div>
            </div>
          </div>
        </div>
        
        <div className="hero-image-container">
          <div className="hero-image-mask"></div>
          <img src="/assets/Background Hero.png" alt="Team working" className="hero-image" />
        </div>
      </section>

      {/* Our Works Section */}
      <section id="works" className="section works-section" data-testid="works-section">
        <span className="section-label">OUR WORKS</span>
        <h2 className="section-title">
          Delivering Scalable Digital Products For <span>Our Partners</span>
        </h2>
        
        <div className="work-cards">
          <div className="work-card" data-testid="work-card-debto">
            <div className="work-card-content">
              <img src="/assets/Logo Debto.png" alt="Debto" className="work-card-logo" />
              <h3>Manage Accounts Payable and Receivable with Ease</h3>
              <p>Manage your debts and receivables with integrated credit scoring and collection assistance.</p>
              <a href="https://debto.id" target="_blank" rel="noopener noreferrer">debto.id</a>
            </div>
            <div className="work-card-mockup-container">
              <img src="/assets/Mockup Debto.png" alt="Debto Mockup" className="work-card-mockup" />
            </div>
          </div>
          
          <div className="work-card" data-testid="work-card-dapurgo">
            <div className="work-card-content">
              <img src="/assets/Logo Dapurgo.png" alt="Dapurgo" className="work-card-logo" />
              <h3>Catering Order Management & Operations System</h3>
              <p>An integrated web solution to streamline catering workflows, from order intake and menu planning to delivery tracking and automated invoicing.</p>
            </div>
            <div className="work-card-mockup-container">
              <img src="/assets/Mockup Dapurgo.png" alt="Dapurgo Mockup" className="work-card-mockup" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section services-section" data-testid="services-section">
        <div className="services-layout">
          <div className="services-top-row">
            <div className="services-text">
              <span className="section-label">OUR SERVICE</span>
              <h2 className="section-title">
                Strategic Design That Drives <span>Business Growth.</span>
              </h2>
            </div>
            <div className="service-card" data-testid="service-card-0">
              <img src="/assets/Icon Web Development.svg" alt="Website Development" className="service-icon" />
              <h4>Website Development</h4>
            </div>
            <div className="service-card" data-testid="service-card-1">
              <img src="/assets/Icon UI UX.svg" alt="UI / UX Consultant" className="service-icon" />
              <h4>UI / UX Consultant</h4>
            </div>
          </div>
          <div className="services-bottom-row">
            <div className="service-card" data-testid="service-card-2">
              <img src="/assets/Icon App.svg" alt="Android / iOS Platform" className="service-icon" />
              <h4>Android / iOS Platform</h4>
            </div>
            <div className="service-card" data-testid="service-card-3">
              <img src="/assets/Icon Dev Ops.svg" alt="Dev Ops" className="service-icon" />
              <h4>Dev Ops</h4>
            </div>
            <div className="service-card" data-testid="service-card-4">
              <img src="/assets/Icon Business Consultant.svg" alt="Business Consultant" className="service-icon" />
              <h4>Business Consultant</h4>
            </div>
            <div className="service-card" data-testid="service-card-5">
              <img src="/assets/Icon IOT Dev.svg" alt="IOT Development" className="service-icon" />
              <h4>IOT Development</h4>
            </div>
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section id="why-us" className="section why-section" data-testid="why-us-section">
        <span className="section-label">WHY US</span>
        <h2 className="section-title">
          How We Help Your <span>Business</span>
        </h2>
        
        <div className="why-grid">
          {/* Left column - Digital Transformation */}
          <div className="why-grid-left">
            <div className="why-card-digital" data-testid="why-card-digital">
              <img src="/assets/Background Digital.png" alt="Digital Transformation" />
              <div className="why-card-content">
                <h4>Digital Transformation & Modernization</h4>
                <p>Validate your brilliant idea into a tested concept, guiding you from thought to a Go-to-Market ready</p>
              </div>
            </div>
          </div>
          
          {/* Right columns */}
          <div className="why-grid-right">
            {/* Row 1: Scalable (326) + Rapid (569) */}
            <div className="why-row">
              <div className="why-card-scalable" data-testid="why-card-scalable">
                <img src="/assets/Scalable.png" alt="Scalable Solutions" />
                <h4>Scalable, Custom Solutions</h4>
                <p>Using best-in-class technology ready to support your future business growth.</p>
              </div>
              
              <div className="why-card-rapid" data-testid="why-card-rapid">
                <img src="/assets/Rapid Execution.png" alt="Rapid Execution" />
                <div className="why-card-content">
                  <h4>Rapid Execution</h4>
                  <p>We turn your product ideas into a market-ready Minimum Viable Product (MVP) quickly and efficiently.</p>
                </div>
              </div>
            </div>
            
            {/* Row 2: Partner (569) + Value-Driven (326) */}
            <div className="why-row">
              <div className="why-card-partner" data-testid="why-card-partner">
                <h5>Our partner with capability</h5>
                <img src="/assets/Google Partner.png" alt="Google Cloud Partner" className="partner-main-logo" />
                <p className="partner-subtitle">
                  Indirect Partner, resell through Datalabs 
                  <img src="/assets/Logo Datalabs.png" alt="Datalabs" style={{ height: '16px', marginLeft: '5px' }} />
                </p>
                <div className="partner-badges">
                  <img src="/assets/Google Cloud 1.png" alt="Google Cloud 1" />
                  <img src="/assets/Google Cloud 2.png" alt="Google Cloud 2" />
                  <img src="/assets/Google Cloud 3.png" alt="Google Cloud 3" />
                  <img src="/assets/Google Cloud 4.png" alt="Google Cloud 4" />
                  <img src="/assets/Google Cloud 5.png" alt="Google Cloud 5" />
                </div>
              </div>
              
              <div className="why-card-innovation" data-testid="why-card-innovation">
                <img src="/assets/Lamp.png" alt="Value-Driven Innovation" />
                <h4>Value-Driven Innovation</h4>
                <p>We design and integrate strategic, results-oriented features.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section testimonials-section" data-testid="testimonials-section">
        <h2 className="section-title">
          Our Client <span>Say</span>
        </h2>

        <div className="testimonials-marquee" aria-label="Client testimonials">
          <div className="testimonials-track">
            {[...testimonials, ...testimonials].map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="testimonial-quote">"</div>
                <p className="testimonial-text">{testimonial.text}</p>
                <div className="testimonial-author">
                  <img src={testimonial.image} alt={testimonial.name} />
                  <div className="testimonial-author-info">
                    <h5>{testimonial.name}</h5>
                    <p>{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src="/assets/Logo BBM White.png" alt="BauBagah Maju" />
            <p>Scale Your Business With High Performance Software</p>
            <button className="btn-cta-white" onClick={openWhatsApp} data-testid="footer-lets-talk-btn">
              Let's talk about your project
            </button>
          </div>
          
          <div className="footer-services">
            <h4>Services</h4>
            <ul>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Website Development</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>UI / UX Consultant</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Android / iOS Platform</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Dev Ops</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Business Consultant</a></li>
              <li><a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>IOT Development</a></li>
            </ul>
          </div>
          
          <div className="footer-contact">
            <h4>Contact Us</h4>
            <div className="footer-contact-item">
              <Mail size={16} />
              <span>beubagahmaju@gmail.com</span>
            </div>
            <div className="footer-contact-item">
              <Phone size={16} />
              <span>+62 857-6007-0056</span>
            </div>
            <div className="footer-contact-item">
              <MapPin size={16} />
              <span>Epi Walk Building 3rd Floor, Jl. HR. Rasuna Said No.306, RT.2/RW.5, Karet Kuningan, Kecamatan Setiabudi, Kota Jakarta Selatan, Daerah Khusus Ibukota Jakarta 12940</span>
            </div>
          </div>
        </div>
        
        <div className="footer-bottom">
          <p>© BauBagahMaju</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
